import { useCustomSearchParams, SetURLSearchParams } from './useCustomSearchParams';
import { useEffect, useMemo, useState } from 'react';
import { IErrorLogger, IRemoteConfigProvider } from '@verv-inc/js-toolkit';

export enum REMOTE_SEARCH_STATE {
	IDLE = 'idle',
	LOADING = 'loading',
	REMOTE_APPLIED = 'remote_applied',
	REMOTE_FAILED = 'remote_fail',
	NO_ID = 'no_id',
}

export interface IResult {
	state: REMOTE_SEARCH_STATE;
	value?: { [k: string]: any };
}

interface Props {
	configSearchKey: string;
	provider: IRemoteConfigProvider;
	errorLogger: IErrorLogger;
	defaultUrl?: string;
}

export const useRemoteSearchParams = (props: Props): [IResult, SetURLSearchParams] => {
	const { configSearchKey, provider, errorLogger, defaultUrl } = props;
	const [search, setSearch] = useCustomSearchParams(defaultUrl);
	const [result, setResult] = useState<IResult>({ state: REMOTE_SEARCH_STATE.IDLE });

	const searchMemo = useMemo(() => {
		return search;
	}, [JSON.stringify(search)]);

	useEffect(() => {
		fetchAndActivateRemoteConfig().catch(errorLogger.logError);
	}, [searchMemo]);

	const fetchAndActivateRemoteConfig = async (): Promise<void> => {
		if (result.state === REMOTE_SEARCH_STATE.LOADING) return;

		const configID = search[configSearchKey];
		if (!configID) {
			setResult({
				state: REMOTE_SEARCH_STATE.NO_ID,
				value: search,
			});
			return;
		}

		setResult({
			state: REMOTE_SEARCH_STATE.LOADING,
		});

		try {
			const config = await provider.getRemoteConfig(configID);
			const combinedSearch = {
				...search,
				...config,
			};
			setResult({
				state: REMOTE_SEARCH_STATE.REMOTE_APPLIED,
				value: combinedSearch,
			});
		} catch {
			setResult({
				state: REMOTE_SEARCH_STATE.REMOTE_FAILED,
				value: search,
			});
		}
	};

	return [result, setSearch];
};
